import React, { useEffect, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Container, Grid, Stack, Typography } from '@mui/material';

import PageTitleWrapper from 'components/PageTitleWrapper';
import ExpiryWarning from 'components/ExpiryWarning';
import TimeWindows from 'components/TimeWindows';
import RefreshTimer from 'components/RefreshTimer';
import EcosystemHeader from 'components/Header/EcosystemHeader';
import {
  geEcosystemForDetail,
  getEcosystemDetailIndex,
  getEcosystemUsage,
  getEcosystemThreatData,
  getEcosystemNetworkMetric,
  getEcosystemThreatMetric,
  getEcosystemDetailCounters,
  setTimeWindow,
  setDetailLoading,
} from 'store/ecosystems/actions';
import { ROUTES } from 'enum';

import Utilization from './Utilization';
import Security from './Security';

const getTimeStamp = (timeWindow) => {
  const date = new Date();
  return {
    to: date.toISOString(),
    from: new Date(date.setHours(date.getHours() - timeWindow)).toISOString(),
  };
};

const timeWindowOptions = [
  { label: '8 hours', value: 8 },
  { label: '24 hours', value: 24 },
];

const EcosystemDetail = ({
  geEcosystemForDetail,
  getEcosystemDetailIndex,
  getEcosystemUsage,
  getEcosystemNetworkMetric,
  getEcosystemThreatMetric,
  getEcosystemThreatData,
  getEcosystemDetailCounters,
  setTimeWindow,
  setDetailLoading,
}) => {
  const history = useHistory();
  const { cusID, ecoID } = useParams();

  const { ecosystem, timeWindow, refresh } = useSelector((state) => state.ecosystems);

  const [loading, setLoading] = useState(true);
  const [intervalId, setIntervalId] = useState(null);
  const [timeStamp, setTimeStamp] = useState(getTimeStamp(timeWindow));

  const handleRefresh = useCallback(() => {
    setTimeStamp(getTimeStamp(timeWindow));
  }, [timeWindow]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh, timeWindow]);

  const getData = useCallback(() => {
    setDetailLoading();
    getEcosystemDetailIndex(timeStamp, ecoID);
    getEcosystemUsage(timeStamp, ecoID);
    getEcosystemNetworkMetric(timeStamp, ecoID, 'application');
    getEcosystemNetworkMetric(timeStamp, ecoID, 'user');
    getEcosystemNetworkMetric(timeStamp, ecoID, 'destination_fqdn');
    getEcosystemNetworkMetric(timeStamp, ecoID, 'source_fqdn');
    getEcosystemThreatMetric(timeStamp, ecoID, 'details');
    getEcosystemThreatMetric(timeStamp, ecoID, 'user');
    getEcosystemThreatMetric(timeStamp, ecoID, 'destination_fqdn');
    getEcosystemThreatMetric(timeStamp, ecoID, 'source_fqdn');
    getEcosystemThreatData(timeStamp, ecoID);
    getEcosystemDetailCounters(timeStamp, ecoID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp, ecoID]);

  useEffect(() => {
    if (ecosystem?.uuid) {
      getData();
    }
  }, [getData, ecosystem?.uuid]);

  useEffect(() => {
    if (intervalId) clearInterval(intervalId);
    if (refresh) {
      const interval = setInterval(() => {
        handleRefresh();
      }, refresh * 60 * 1000);
      setIntervalId(interval);
    }
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const getEcosystem = useCallback(async () => {
    try {
      setLoading(true);
      await geEcosystemForDetail({ ecosystemUUID: ecoID });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [geEcosystemForDetail, ecoID]);

  useEffect(() => {
    getEcosystem();
  }, [getEcosystem]);

  const handleChangeTab = (tab) => {
    if (tab === 'summary') history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems/dashboard`);
    else if (tab === 'threat') history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems/threat-report`);
    else history.replace(`${ROUTES.CUSTOMERS}/${cusID}/ecosystems`);
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - {ecosystem?.name ?? ''} - Sectrify</title>
      </Helmet>
      <div className="wrapper ecosystems-wrapper">
        <ExpiryWarning />
        <EcosystemHeader onChangeTab={handleChangeTab} activeTab="summary" />
        <div style={{ width: '100%' }}>
          <PageTitleWrapper size="false">
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              rowGap={4}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', lg: 'center' }}
            >
              <Stack direction="row" alignItems="center">
                <Typography variant="h4" noWrap title={ecosystem?.name ?? ''} sx={{ pr: 4, maxWidth: 480 }}>
                  {ecosystem ? ecosystem.name : ''}
                </Typography>
                <TimeWindows
                  disabled={loading}
                  options={timeWindowOptions}
                  value={timeWindow}
                  onChange={(value) => setTimeWindow(value)}
                />
              </Stack>
              <RefreshTimer disabled={loading} value={refresh} onRefresh={handleRefresh} />
            </Stack>
          </PageTitleWrapper>
          <Container maxWidth="false">
            {!ecosystem.uuid || loading ? (
              <Box sx={{ height: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={4}>
                <Grid item md={12} lg={6}>
                  <Utilization timeStamp={timeStamp} />
                </Grid>
                <Grid item md={12} lg={6}>
                  <Security timeStamp={timeStamp} />
                </Grid>
              </Grid>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

EcosystemDetail.propTypes = {
  geEcosystemForDetail: PropTypes.func,
  getEcosystemDetailIndex: PropTypes.func,
  getEcosystemUsage: PropTypes.func,
  getEcosystemThreatData: PropTypes.func,
  getEcosystemNetworkMetric: PropTypes.func,
  getEcosystemThreatMetric: PropTypes.func,
  getEcosystemDetailCounters: PropTypes.func,
  setTimeWindow: PropTypes.func,
  setDetailLoading: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
  geEcosystemForDetail: (timeWindow) => dispatch(geEcosystemForDetail(timeWindow)),
  getEcosystemDetailIndex: (timeStamp, ecosystemUUID) =>
    dispatch(getEcosystemDetailIndex({ timeStamp, ecosystemUUID })),
  getEcosystemUsage: (timeStamp, ecosystemUUID) => dispatch(getEcosystemUsage({ timeStamp, ecosystemUUID })),
  getEcosystemThreatData: (timeStamp, ecosystemUUID) => dispatch(getEcosystemThreatData({ timeStamp, ecosystemUUID })),
  getEcosystemNetworkMetric: (timeStamp, ecosystemUUID, attributes) =>
    dispatch(getEcosystemNetworkMetric({ timeStamp, ecosystemUUID, attributes })),
  getEcosystemThreatMetric: (timeStamp, ecosystemUUID, attributes) =>
    dispatch(getEcosystemThreatMetric({ timeStamp, ecosystemUUID, attributes })),
  getEcosystemDetailCounters: (timeStamp, ecosystemUUID) =>
    dispatch(getEcosystemDetailCounters({ timeStamp, ecosystemUUID })),
  setDetailLoading: () => dispatch(setDetailLoading()),
  setTimeWindow: (value) => dispatch(setTimeWindow(value)),
});

export default connect(null, mapDispatchToProps)(EcosystemDetail);
